import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

const SchemaEditor = ({ schema, onChange, templateId = null, llmInstruction = '', onGenerateSchema = null, isGeneratingSchema = false }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [editingFieldName, setEditingFieldName] = useState(null)

  // Helper: Convert old schema format to new format with display_title
  const normalizeSchema = (rawSchema) => {
    const normalized = {}
    Object.entries(rawSchema).forEach(([key, value]) => {
      normalized[key] = {
        type: value.type || 'string',
        description: value.description || '',
        display_title: value.display_title || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        required: value.required || false
      }
    })
    return normalized
  }

  const normalizedSchema = normalizeSchema(schema)

  const handlePropertyChange = (propertyName, field, value) => {
    onChange({
      ...schema,
      [propertyName]: {
        ...schema[propertyName],
        [field]: value
      }
    })
  }

  const handleRenameField = (oldName, newName) => {
    if (newName && newName !== oldName && !schema[newName]) {
      const newSchema = {}
      Object.entries(schema).forEach(([key, value]) => {
        if (key === oldName) {
          newSchema[newName] = value
        } else {
          newSchema[key] = value
        }
      })
      onChange(newSchema)
      setEditingFieldName(null)
      toast.success(`Field renamed: ${oldName} → ${newName}`)
    }
  }

  const handleAddProperty = () => {
    const newPropertyName = prompt(t('schemaEditor.promptPropertyName'))
    if (newPropertyName && !schema[newPropertyName]) {
      onChange({
        ...schema,
        [newPropertyName]: {
          type: 'string',
          description: '',
          display_title: newPropertyName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          required: false
        }
      })
    }
  }

  const handleDeleteProperty = (propertyName) => {
    if (confirm(t('schemaEditor.confirmDelete', { name: propertyName }))) {
      const newSchema = { ...schema }
      delete newSchema[propertyName]
      onChange(newSchema)
    }
  }

  const handleExportSchema = async () => {
    try {
      // Export as JSON file
      const exportData = {
        schema_version: "1.0",
        exported_at: new Date().toISOString(),
        fields: Object.entries(normalizedSchema).map(([fieldName, fieldData]) => ({
          field_name: fieldName,
          display_title: fieldData.display_title,
          type: fieldData.type,
          description: fieldData.description,
          required: fieldData.required
        }))
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `schema-export-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success('✅ Schema exported successfully!')
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('❌ Failed to export schema')
    }
  }

  const handleImportSchema = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      try {
        const file = e.target.files[0]
        if (!file) return

        const text = await file.text()
        const importData = JSON.parse(text)

        // Convert imported fields to schema format
        const newSchema = {}
        importData.fields.forEach(field => {
          newSchema[field.field_name] = {
            type: field.type,
            description: field.description,
            display_title: field.display_title,
            required: field.required
          }
        })

        onChange(newSchema)
        toast.success(`✅ Schema imported: ${importData.fields.length} fields`)
      } catch (error) {
        console.error('Import failed:', error)
        toast.error('❌ Failed to import schema')
      }
    }
    input.click()
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-dark-300 flex items-center">
          <i className="fa-solid fa-table-columns text-primary-500 mr-2"></i>
          {t('schemaEditor.title')}
          <span className="ml-3 px-3 py-1 bg-primary-500/10 text-primary-800 text-sm font-bold rounded-full">
            {Object.keys(schema).length} {t('schemaEditor.fields')}
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {onGenerateSchema && (
            <button
              type="button"
              disabled={isGeneratingSchema}
              onClick={() => {
                if (!llmInstruction?.trim()) {
                  toast.error(t('schemaEditor.generateAiNeedInstruction'))
                  return
                }
                onGenerateSchema(llmInstruction)
              }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded flex items-center gap-1 disabled:opacity-70 disabled:cursor-wait"
              title={t('schemaEditor.generateWithAi')}
            >
              {isGeneratingSchema ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  {t('schemaEditor.generateLoading')}
                </>
              ) : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  {t('schemaEditor.generateWithAi')}
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleExportSchema}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded flex items-center gap-1"
            title="Export Schema"
          >
            <i className="fa-solid fa-download"></i>
            Export
          </button>
          <button
            type="button"
            onClick={handleImportSchema}
            className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded flex items-center gap-1"
            title="Import Schema"
          >
            <i className="fa-solid fa-upload"></i>
            Import
          </button>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary-500 hover:text-primary-800 font-semibold flex items-center"
          >
            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} mr-2`}></i>
            {isExpanded ? t('schemaEditor.collapse') : t('schemaEditor.expand')}
          </button>
        </div>
      </div>

      {!isExpanded && (
        <div>
          <button 
            type="button"
            onClick={() => setIsExpanded(true)}
            className="w-full px-4 py-3 bg-light-100 hover:bg-light-200/20 border-2 border-light-200/30 rounded-lg font-semibold text-dark-300 transition-all flex items-center justify-center"
          >
            <i className="fa-solid fa-chevron-down mr-2"></i>
            {t('schemaEditor.expand')}
          </button>
          
          <p className="text-sm text-dark-100 mt-3 text-center">{t('schemaEditor.expandHint')}</p>
          
          <div className="mt-6 grid grid-cols-2 gap-3">
            {Object.entries(normalizedSchema).slice(0, 6).map(([fieldName, fieldData]) => (
              <div key={fieldName} className="flex items-center p-3 bg-light-100 rounded-lg border border-light-200/20">
                <i className={`fa-solid ${
                  fieldName.includes('phone') || fieldName.includes('telephone') ? 'fa-phone' :
                  fieldName.includes('email') ? 'fa-envelope' :
                  fieldName.includes('city') || fieldName.includes('location') ? 'fa-location-dot' :
                  fieldName.includes('office') || fieldName.includes('building') ? 'fa-building' :
                  fieldName.includes('street') || fieldName.includes('address') ? 'fa-map-marker-alt' :
                  fieldName.includes('hours') || fieldName.includes('time') ? 'fa-clock' :
                  'fa-check-circle'
                } ${fieldData.required ? 'text-primary-500' : 'text-primary-800'} mr-3`}></i>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-dark-300">{fieldData.display_title}</div>
                  <div className="text-xs text-dark-100">{fieldData.type}</div>
                </div>
                <i className="fa-solid fa-check-circle text-primary-500"></i>
              </div>
            ))}
          </div>
          
          {Object.keys(schema).length > 6 && (
            <div className="mt-4 text-center">
              <button 
                type="button"
                onClick={() => setIsExpanded(true)}
                className="text-sm text-primary-500 hover:text-primary-800 font-semibold"
              >
                + {t('schemaEditor.showMore', { count: Object.keys(schema).length - 6 })}
              </button>
            </div>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="space-y-4">
          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="text-blue-900 font-medium mb-1">{t('schemaEditor.whatIsThis')}</p>
            <p className="text-blue-700 text-xs">
              {t('schemaEditor.whatIsThisDesc')}
            </p>
          </div>

          {/* Schema Fields */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Object.entries(normalizedSchema).map(([propertyName, propertyData]) => (
              <div key={propertyName} className="bg-light-100 rounded-lg p-4 border border-light-200/20">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-3">
                    {/* Field Name (editable) */}
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-medium">Field Name (Key)</label>
                      {editingFieldName === propertyName ? (
                        <input
                          type="text"
                          defaultValue={propertyName}
                          autoFocus
                          onBlur={(e) => handleRenameField(propertyName, e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameField(propertyName, e.target.value)
                            }
                          }}
                          className="w-full px-2 py-1 border-2 border-primary-500 rounded text-sm font-mono"
                        />
                      ) : (
                        <div
                          onClick={() => setEditingFieldName(propertyName)}
                          className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-2 py-1"
                        >
                          <span className="font-mono text-sm font-semibold text-primary-800">
                            {propertyName}
                          </span>
                          <i className="fa-solid fa-pencil text-xs text-gray-400"></i>
                        </div>
                      )}
                    </div>

                    {/* Display Title */}
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-medium">Display Title</label>
                      <input
                        type="text"
                        value={propertyData.display_title || ''}
                        onChange={(e) => handlePropertyChange(propertyName, 'display_title', e.target.value)}
                        placeholder="z.B. Jugendamt"
                        className="w-full px-3 py-2 border-2 border-light-200/30 rounded text-sm focus:ring-2 focus:ring-primary-500 text-dark-300"
                      />
                    </div>

                    {/* Type Dropdown */}
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-medium">Type</label>
                      <select
                        value={propertyData.type || 'string'}
                        onChange={(e) => handlePropertyChange(propertyName, 'type', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-light-200/30 rounded text-sm focus:ring-2 focus:ring-primary-500 text-dark-300"
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                        <option value="array">Array</option>
                        <option value="object">Object</option>
                        <option value="date">Date</option>
                      </select>
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-medium">Description</label>
                      <textarea
                        value={propertyData.description}
                        onChange={(e) => handlePropertyChange(propertyName, 'description', e.target.value)}
                        rows={2}
                        placeholder={t('schemaEditor.descriptionPlaceholder')}
                        className="w-full px-3 py-2 border-2 border-light-200/30 rounded text-sm focus:ring-2 focus:ring-primary-500 text-dark-300"
                      />
                    </div>

                    {/* Required Checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={propertyData.required}
                        onChange={(e) => handlePropertyChange(propertyName, 'required', e.target.checked)}
                        className="w-4 h-4 text-primary-500 rounded"
                      />
                      <span className="text-sm text-dark-300">
                        {t('schemaEditor.requiredField')}
                      </span>
                    </label>
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleDeleteProperty(propertyName)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete field"
                  >
                    <i className="fa-solid fa-trash text-lg"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add Field Button */}
          <button
            type="button"
            onClick={handleAddProperty}
            className="w-full py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-medium flex items-center justify-center gap-2 shadow-md"
          >
            <i className="fa-solid fa-plus"></i>
            {t('schemaEditor.addNewField')}
          </button>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              onClick={() => {
                if (confirm(t('schemaEditor.confirmReset'))) {
                  toast.info(t('schemaEditor.resetInfo'))
                }
              }}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              {t('schemaEditor.resetToDefault')}
            </button>
            <button
              type="button"
              onClick={() => {
                const requiredCount = Object.values(normalizedSchema).filter(p => p.required).length
                toast.info(t('schemaEditor.schemaSummary', { 
                  total: Object.keys(schema).length, 
                  required: requiredCount, 
                  optional: Object.keys(schema).length - requiredCount 
                }))
              }}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {t('schemaEditor.showSummary')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!Object.keys(schema).length) {
                  toast.info(t('schemaEditor.clearAllAlreadyEmpty'))
                  return
                }
                if (confirm(t('schemaEditor.confirmClearAll'))) {
                  onChange({})
                  toast.success(t('schemaEditor.clearAllSuccess'))
                }
              }}
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              {t('schemaEditor.clearAllFields')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchemaEditor
